import posthog from 'posthog-js'
import { isMarketingPath } from './marketing-paths'

/**
 * PostHog setup. Three surfaces share one project, separated by the
 * `surface` super-property rather than by project — see
 * docs/analytics/README.md for why, and for the event list.
 *
 * Surface is resolved from the pathname at boot, so the marketing router
 * (`src/router.tsx`) and the app router (`src/app-router.tsx`) can both call
 * `initPostHog()` with no argument and get the right configuration.
 */
export type Surface = 'marketing' | 'app' | 'client'

/**
 * Token- and slug-addressed surfaces shown to our customers' OWN clients:
 * briefings, proposals, and the project portal. Anonymous third parties who
 * never agreed to anything — never `identify()`d, and their events carry no
 * PII, only the workspace the activity belongs to.
 */
const CLIENT_PREFIXES = ['/b/', '/p/', '/c/'] as const

export const resolveSurface = (pathname: string): Surface => {
  if (isMarketingPath(pathname)) {
    return 'marketing'
  }
  if (CLIENT_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return 'client'
  }
  return 'app'
}

/**
 * Session-replay masking. Deliberately permissive: only passwords are
 * masked. `maskAllInputs` defaults to `true` in posthog-js, and turning it
 * off does NOT keep password fields safe — `maskInputOptions.password` is a
 * separate switch, and without it `/sign-in` and `/sign-up` would record
 * credentials in plaintext.
 *
 * Everything is in this one constant so tightening a surface later is a
 * single edit. The likeliest future change is masking text on the `/b`
 * briefing form, where the typed content belongs to a third party rather
 * than to one of our own users.
 */
const REPLAY_MASKING = {
  maskAllInputs: false,
  maskInputOptions: { password: true },
} as const

/**
 * Escape hatch for exercising PostHog-driven UI on a dev server. The support
 * widget is the reason it exists: it is rendered by the SDK off remote config,
 * so there is no way to see it — or to check that its requests survive the
 * `/ingest` proxy — with analytics switched off.
 *
 * Opt in per machine with `VITE_POSTHOG_DEV=true` in the root `.env.local`
 * (gitignored), alongside `VITE_POSTHOG_KEY`. Off by default, so a checkout
 * that merely has the key set still captures nothing in dev.
 *
 * **It writes to the real project.** There is only one, and the widget can
 * only load from the project where Support is enabled — so a dev session adds
 * genuine pageviews and identifies. Replay is forced off below to keep the
 * costliest noise out. Turn the flag off when you are done.
 */
const DEV_ANALYTICS = import.meta.env.DEV && import.meta.env.VITE_POSTHOG_DEV === 'true'

/**
 * Planno's own email domain. Anyone signing in with one is us — staff,
 * founders, the seeded dev account — not a customer.
 */
const INTERNAL_EMAIL_DOMAIN = '@planno.app'

/**
 * Whether an address belongs to Planno rather than to a customer. Exported
 * because `AuthBridge` is where the user doc lives; the rule itself belongs
 * next to the suppression it drives.
 */
export const isInternalEmail = (email: string | null | undefined): boolean =>
  typeof email === 'string' && email.trim().toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN)

let initialized = false
/**
 * The surface this page booted on. `identifyUser` and `stopCapturing` are
 * app-only and consult it, because `AuthBridge` — which drives both — also
 * mounts on `/b`, `/p`, and `/c`: those routes are served by the app router,
 * not the marketing one. Without this gate an anonymous client opening a
 * briefing link trips the unauthenticated branch and resets the session on
 * every page load.
 */
let surface: Surface | null = null
/**
 * Whether this instance is currently allowed to send. The app surface boots
 * opted OUT (see `initPostHog`) and only opts in once the impersonation-safe
 * identity resolves; the other surfaces have nobody to impersonate and start
 * opted in.
 */
let capturing = false
let identifiedUserId: string | null = null
/**
 * The user whose support-widget identity has been verified, so a re-render
 * doesn't re-sign the same session — `setIdentity` calls `posthog.alias()`
 * internally, and it reloads the widget's ticket list.
 */
let verifiedSupportUserId: string | null = null
/**
 * Whether replay is currently suppressed for a Planno-internal account. Only
 * ever true because `applyReplaySuppression` set it.
 */
let replaySuppressed = false
/**
 * Whether init switched replay ON for this surface at all — false on
 * marketing and in a `DEV_ANALYTICS` session. The suppression below must
 * never argue with it: without this, an internal user signing out and a
 * customer signing in on the same tab would take the re-enable branch and
 * turn recording on somewhere `initPostHog` deliberately had it off.
 */
let replayConfigured = false

/**
 * Keep our own sessions out of session replay.
 *
 * Staff admins and anyone on `@planno.app` are the people who use the product
 * hardest and least like a customer — dogfooding, reproducing a ticket,
 * clicking through a fix. Their recordings crowd out the ones replay exists
 * to surface, and they are the sessions most likely to have a customer's data
 * on screen.
 *
 * Events are deliberately left alone: they carry `surface` and the workspace
 * group, so an insight can filter internal activity out after the fact.
 * A replay cannot be un-recorded, so that one is decided here.
 *
 * `stopSessionRecording()` is just `set_config({ disable_session_recording:
 * true })` — no guard on the recorder script having loaded — so this is safe
 * to call while the instance is still opted out, which is exactly when
 * `identifyUser` calls it. The recorder then never starts at opt-in, rather
 * than starting and being torn down a frame later.
 *
 * No-ops entirely on a surface booted with replay off. Both halves matter:
 * there is nothing to suppress, and — the reason it is a guard rather than
 * an optimisation — nothing here may hand recording BACK to a surface that
 * never had it.
 */
const applyReplaySuppression = (internal: boolean) => {
  if (!(initialized && replayConfigured) || internal === replaySuppressed) {
    return
  }
  if (internal) {
    posthog.stopSessionRecording()
  } else {
    // Only reachable after a suppression of ours, on a surface where replay
    // was on to begin with — a second account signing in on the same browser
    // without a reload.
    posthog.startSessionRecording()
  }
  replaySuppressed = internal
}

/**
 * Identity and consent transitions only exist on the app surface. Marketing
 * has no auth at all, and client surfaces are anonymous third parties we
 * deliberately never identify.
 */
const identityTransitionsApply = () => initialized && surface === 'app'

/**
 * Grouping, unlike identity, applies to the client surfaces too.
 *
 * A `/b`, `/p` or `/c` visitor is still never `identify()`d — they are a third
 * party with no account to be — but their events DO belong to a workspace, and
 * that is the question those pages exist to answer. Marketing is the one
 * surface with no workspace to name.
 */
const groupsApply = () => initialized && surface !== 'marketing'

/**
 * The group type registered in PostHog — see the group-analytics section of
 * docs/analytics/README.md. Singular by PostHog's convention, and one of the
 * five a project is allowed.
 */
const WORKSPACE_GROUP_TYPE = 'workspace'

/**
 * The workspace the UI has asked to be in scope, and the one posthog-js has
 * actually been told about. They differ for exactly one window: the app
 * surface boots opted OUT, and the workspace layout can resolve a workspace
 * before `identifyUser` opts in. Registering a group while opted out would
 * put the association in persistence and swallow its `$groupidentify`, after
 * which posthog-js considers the key unchanged and never sends another — so
 * the request is held here and applied on opt-in instead.
 */
let requestedWorkspaceId: string | null = null
let appliedWorkspaceId: string | null = null

const applyWorkspaceGroup = () => {
  if (!(groupsApply() && capturing)) {
    return
  }
  if (requestedWorkspaceId === null || requestedWorkspaceId === appliedWorkspaceId) {
    return
  }
  // Deliberately WITHOUT properties. posthog-js sends a billable
  // `$groupidentify` whenever the key changes OR properties are passed, so
  // passing them here would bill one event per workspace switch to restate
  // facts the browser is not the source of truth for. Convex owns the
  // properties (`workspaceGroups.sync`), which is also what makes the group
  // legible in PostHog's UI — it labels a group by `name`, and this key is an
  // opaque id.
  posthog.group(WORKSPACE_GROUP_TYPE, requestedWorkspaceId)
  appliedWorkspaceId = requestedWorkspaceId
}

/**
 * Put a workspace in scope, so every subsequent event carries `$groups`.
 *
 * Keyed by workspace **id**, not slug: a group key is permanent — PostHog
 * lets you delete a group type but never remap its keys — so it has to be the
 * one handle that cannot change.
 *
 * This is the ONLY workspace attribution an event needs — don't also pass a
 * `workspace_slug` property. That holds on the anonymous `/b`, `/p` and `/c`
 * surfaces too, which call this with the id their public query returns.
 *
 * Safe to call on every render: it no-ops unless the workspace changed, and
 * it holds the request until consent resolves.
 */
export const setWorkspaceGroup = (workspaceId: string) => {
  requestedWorkspaceId = workspaceId
  applyWorkspaceGroup()
}

/**
 * Initialise PostHog for the surface implied by the current URL.
 *
 * No-ops during SSR/prerender, in dev (unless `DEV_ANALYTICS` opts this
 * machine in), and when `VITE_POSTHOG_KEY` is unset — which also makes the
 * Playwright e2e suite a no-op with no test-side configuration.
 */
export const initPostHog = () => {
  if (initialized || typeof window === 'undefined' || (import.meta.env.DEV && !DEV_ANALYTICS)) {
    return
  }
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) {
    return
  }

  surface = resolveSurface(window.location.pathname)
  // Replay runs on the product and on the client-facing surfaces, never on
  // the prerendered marketing pages: recording every anonymous bounce and bot
  // buys nothing and costs a lot. Never in dev either — a recording of
  // localhost with the devtools open is noise in a real project. Held in a
  // local so `applyReplaySuppression` and the init config below cannot drift
  // apart.
  const replayDisabled = surface === 'marketing' || DEV_ANALYTICS

  posthog.init(key, {
    // Proxied through our own origin (Nitro `routeRules` in vite.config.ts)
    // because ad blockers block `*.posthog.com` aggressively — and it is
    // marketing traffic, the numbers used to judge ad spend, that gets eaten.
    api_host: '/ingest',
    autocapture: true,
    // Pins the SDK's behavioural defaults (pageview capture on history
    // change, person-profile handling). Bumping this string changes runtime
    // behaviour — treat it as a deliberate upgrade, not a version bump.
    defaults: '2026-06-25',
    // The support chat widget (`posthog.conversations`) renders itself as soon
    // as remote config says it is enabled, on every surface — so the only
    // place to exclude one is here. It is a channel for CUSTOMERS, which
    // leaves the product and nothing else:
    //
    // - `marketing` is prospects. Sales questions belong in a sales channel,
    //   not in the support queue.
    // - `client` — `/b`, `/p`, `/c` — belongs to our customers' OWN clients. A
    //   Planno bubble on an architect's proposal page misrepresents whose page
    //   it is, and every ticket it opened would be from someone we cannot
    //   help.
    //
    // Disabling here rather than hiding means those surfaces never fetch the
    // widget bundle at all. Within the app it is narrowed further — to signed
    // -in screens outside onboarding — but that depends on state this early
    // boot has no access to, so it is a CSS gate: `useSupportWidgetGate`.
    //
    // Everything else about the widget — colour, position, greeting, whether
    // an email is required — is PostHog dashboard config, not code.
    disable_conversations: surface !== 'app',
    disable_session_recording: replayDisabled,
    // The app defers consent until `AuthBridge` knows whether this is a staff
    // impersonation session. Without this, the first `$pageview` of an
    // impersonation boot lands under the customer before we can suppress it.
    opt_out_capturing_by_default: surface === 'app',
    // `always` everywhere except marketing, and the reason is grouping rather
    // than identity: PostHog links an event to a group only when
    // `$process_person_profile` is true, so `identified_only` on a surface
    // that never calls `identify()` means its events can never belong to a
    // workspace. That is the whole question `/b`, `/p` and `/c` exist to
    // answer — who is reading this studio's proposals — so the profile is
    // switched on deliberately, knowing identified events bill more. Those
    // visitors are still never `identify()`d; a profile here is a browser,
    // not a person we can name.
    //
    // Marketing stays `identified_only`: it has no workspace to group by, and
    // it is the highest-volume anonymous traffic we have — one profile per
    // bounced ad click would be pure cost for a dimension that does not
    // exist.
    person_profiles: surface === 'marketing' ? 'identified_only' : 'always',
    session_recording: REPLAY_MASKING,
    ui_host: 'https://us.posthog.com',
  })
  posthog.register({ surface })

  capturing = surface !== 'app'
  replayConfigured = !replayDisabled
  initialized = true
}

/**
 * Whether PostHog is live. Only for wiring that needs the client itself
 * rather than an event — currently just the Sentry cross-link.
 */
export const isPostHogActive = () => initialized

/** Record an event. No-ops when PostHog never initialised. */
export const capture = (event: string, properties?: Record<string, unknown>) => {
  if (!initialized) {
    return
  }
  posthog.capture(event, properties)
}

/**
 * Bind the session to a signed-in user and, on the app surface, take it out
 * of the opted-out boot state.
 *
 * `opt_in_capturing` emits `$opt_in`, never a `$pageview`, so the pageview
 * suppressed at boot has to be replayed by hand — otherwise every authed
 * session is missing its landing pageview.
 *
 * `supportIdentity` is the signed pair from `api.support.identityHash`, which
 * lets the support widget find this user's tickets on any device instead of
 * only in the browser that opened them. Optional in every sense: null until
 * the query answers, and null forever on a deployment with no
 * `POSTHOG_SECRET_API_TOKEN`, where the widget stays per-browser.
 *
 * `internal` marks one of us rather than a customer — see
 * `applyReplaySuppression`, which it drives. It is applied BEFORE the opt-in
 * below so an internal session never records a single frame.
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, unknown>,
  supportIdentity?: { distinctId: string; hash: string } | null,
  internal = false
) => {
  if (!identityTransitionsApply()) {
    return
  }
  applyReplaySuppression(internal)
  if (!capturing) {
    posthog.opt_in_capturing({ captureEventName: false })
    capturing = true
    posthog.capture('$pageview')
  }
  if (identifiedUserId !== userId) {
    posthog.identify(userId, properties)
    identifiedUserId = userId
  }
  // Strictly after `identify`, and only for the id we just identified.
  // `setIdentity` aliases the current `distinct_id` to the one it is given, so
  // a hash minted for a different subject would merge two person profiles —
  // there is no undo for that. Matching ids make the alias a no-op, which is
  // the only shape we allow.
  if (supportIdentity?.distinctId === userId && verifiedSupportUserId !== userId) {
    posthog.setIdentity(userId, supportIdentity.hash)
    verifiedSupportUserId = userId
  }
  // Strictly after the opt-in above. A workspace resolved during the
  // opted-out window is held rather than registered (see
  // `applyWorkspaceGroup`), and this is the edge that releases it.
  applyWorkspaceGroup()
}

/**
 * Stop capturing and drop the person association — for sign-out, for staff
 * impersonation, and for the `/admin` surface.
 *
 * `reset()` does both jobs: it clears the identity AND returns the instance
 * to its default consent state, which on the app surface is opted out. That
 * also fixes the ordering rule posthog-js documents — reset must come BEFORE
 * a later `opt_in_capturing`, never after, or capture stops silently.
 *
 * Guarded twice over: app-surface-only (see `identityTransitionsApply` — the
 * caller also mounts on the anonymous client surfaces), and no-op unless
 * there is something to stop, so repeated calls (an `/admin` route
 * re-rendering) don't each mint a fresh `distinct_id` via `reset()`.
 *
 * It also takes the support widget down with it. `clearIdentity` drops the
 * verified identity so the next visitor on this browser cannot read the
 * previous user's tickets, and `reset()` tears the widget out of the DOM until
 * the page reloads — remote config fires once per document, so nothing
 * re-renders it. That costs an anonymous chat on `/sign-in` immediately after
 * a sign-out, and buys not leaking a support thread across accounts. A cold
 * anonymous boot is unaffected: the guard above returns before either call.
 */
export const stopCapturing = () => {
  if (!(identityTransitionsApply() && (capturing || identifiedUserId || verifiedSupportUserId))) {
    return
  }
  posthog.clearIdentity()
  posthog.reset()
  capturing = false
  identifiedUserId = null
  verifiedSupportUserId = null
  // `reset()` clears persistence, and `$groups` lives there — so both halves
  // of the group state go with it. Dropping the REQUEST too (not just what
  // was applied) is what makes an `/admin` visit mid-session safe: staff
  // browsing away and back re-enters through the workspace layout, which
  // re-requests, rather than silently reusing whichever workspace was in
  // scope before the suppression.
  requestedWorkspaceId = null
  appliedWorkspaceId = null
  // `replaySuppressed` deliberately survives: it mirrors posthog-js's own
  // `disable_session_recording` config, which `reset()` does not touch.
  // Clearing it here would strand a suppressed instance with no way back —
  // the next (external) user on this browser would see recording stay off.
}
